import React, { useEffect, useState } from "react";
import Select from 'react-select';
import moment from 'moment';
import PanelTemplate from "../../templates/Panel";
import Table from "../../components/Table";
import Box from "../../components/Box";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import PageHeader from "../../components/PageHeader";
import FilterSimple from "../../components/FilterSimple";
import DotStatus from "../../components/DotStatus";
import { useNavigate, useSearchParams } from "react-router-dom";
import InputColor from "../../components/InputColor";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import Input from "../../components/Input";
import InputSelect from "../../components/InputSelect";
import InputFile from "../../components/InputFile";
import { toast } from "react-toastify";
import { canUseGestao } from "../../services/auth";
import DotColor from "../../components/DotColor";
import { ModalDelete } from "../../components/ModalDelete";
import BoxTabs from "../../components/BoxTabs";
import Icon from "../../components/Icon";
import { PURCHASE_ORDER_STATUS_RULES, sortStatuses } from "../PurchaseOrder/helpers";

const defaultOptions = [
    {label: 'Sim', value: true},
    {label: 'Não', value: false}
];

const typeOptions = [
    { label: 'Pedido de venda', value: 'order' },
    { label: 'Orçamento', value: 'budget' },
    { label: 'Pedido de compra', value: 'purchase_order' }
];

// Ordem das abas da tela; o `?tab=` da URL usa o mesmo valor do tipo.
const TABS = ['budget', 'order', 'purchase_order'];

// Códigos de situação aceitos pela API do Tiny ERP, gravados em
// `status.tiny_situacao` e enviados como situação do pedido (ver
// App\Services\Transformers\OrderToPedidoDTOTransformer no backend).
const tinyKeyOptions = [
    { label: 'Nenhum (não sincroniza com o Tiny)', value: null },
    { label: 'Em aberto', value: 'aberto' },
    { label: 'Aprovado', value: 'aprovado' },
    { label: 'Preparando envio', value: 'preparando_envio' },
    { label: 'Faturado', value: 'faturado' },
    { label: 'Pronto para envio', value: 'pronto_envio' },
    { label: 'Enviado', value: 'enviado' },
    { label: 'Entregue', value: 'entregue' },
    { label: 'Não entregue', value: 'nao_entregue' },
    { label: 'Cancelado', value: 'cancelado' },
    { label: 'Outro', value: 'outro' }
];

// Status controlados pelo sistema, mapeados pelo campo `status.key`.
// Têm comportamento especial (são padrão e/ou disparam automações), então
// exibimos uma explicação em vez de tratá-los como um status comum.
const STATUS_INFO = {
    aguardando_orcamento: {
        badge: 'Padrão',
        badgeIcon: 'alert',
        badgeType: 'locked',
        title: 'Aguardando orçamento',
        description: [
            'Este é o status inicial e padrão de todo orçamento criado na plataforma. Por ser um status de sistema, ele não pode ser editado nem excluído.',
            'Assim que uma proposta é enviada ao cliente, o orçamento sai automaticamente deste status e passa para "Pendente de aprovação".'
        ]
    },
    pendente_aprovacao: {
        badge: 'Automação',
        badgeIcon: 'zap',
        badgeType: 'automation',
        title: 'Pendente de aprovação',
        description: [
            'Este status é controlado por automações do sistema:',
            '• Ao enviar uma proposta de um orçamento que estava em "Aguardando orçamento", ele passa automaticamente para este status.',
            '• Se um pedido gerado a partir de um orçamento for cancelado ou excluído, o orçamento volta automaticamente para este status.',
            'Você pode ajustar o nome e a cor, mas o comportamento das automações permanece vinculado a ele.'
        ]
    }
};

// Status de pedido de compra com regra no sistema (a key vem da migration e
// não é editável). Nome e cor podem mudar; excluir, não.
const PURCHASE_ORDER_STATUS_INFO = {
    draft: { badge: 'Inicial', badgeIcon: 'lock', badgeType: 'locked' },
    sent: { badge: 'Automação', badgeIcon: 'zap', badgeType: 'automation' },
    invoiced: { badge: 'Automação', badgeIcon: 'zap', badgeType: 'automation' },
    canceled: { badge: 'Sistema', badgeIcon: 'lock', badgeType: 'locked' },
};

const getStatusInfo = status => status.type === 'purchase_order'
    ? PURCHASE_ORDER_STATUS_INFO[status.key]
    : STATUS_INFO[status.key];

export const StatusList = () => {
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState('');
    const [statuses, setStatuses] = useState([]);
    const [data, setData] = useState({header: [], rows: []});

    const [showCreateStatus, setShowCreateStatus] = useState(false);
    const [showEditStatus, setShowEditStatus] = useState(false);

    const [statusId, setStatusId] = useState(null);
    const [statusName, setStatusName] = useState('');
    const [statusType, setStatusType] = useState('budget');
    const [statusDefault, setStatusDefault] = useState(false);
    const [statusColor, setStatusColor] = useState('');
    const [statusKey, setStatusKey] = useState(null);
    const [statusTinySituacao, setStatusTinySituacao] = useState(null);

    // Status de pedido de compra só existem com o módulo Gestão.
    const hasGestao = canUseGestao();
    const tabs = hasGestao ? TABS : TABS.filter(type => type !== 'purchase_order');
    const statusTypeOptions = hasGestao ? typeOptions : typeOptions.filter(type => type.value !== 'purchase_order');

    const [searchParams, setSearchParams] = useSearchParams();
    const currentTab = Math.max(tabs.indexOf(searchParams.get('tab')), 0);
    const changeTab = index => setSearchParams(index === 0 ? {} : { tab: tabs[index] }, { replace: true });
    const isEditingPurchaseOrderStatus = statusType === 'purchase_order';
    const editingSystemRule = isEditingPurchaseOrderStatus ? PURCHASE_ORDER_STATUS_RULES[statusKey] : null;

    const [statusToDelete, setStatusToDelete] = useState(null);

    const [showInfoStatus, setShowInfoStatus] = useState(false);
    const [infoStatus, setInfoStatus] = useState(null);

    // Códigos conhecidos do Tiny; se o código não estiver entre eles, é um valor
    // customizado digitado pelo tenant (equivalente ao texto livre que se
    // enviava antes desta tela, via `pagamento_status`).
    const knownTinyKeys = tinyKeyOptions.map(option => option.value).filter(value => value !== 'outro');
    const isCustomTinyKey = statusType === 'order' && statusTinySituacao !== null && !knownTinyKeys.includes(statusTinySituacao);
    const tinySelectValue = isCustomTinyKey ? 'outro' : statusTinySituacao;
    const handleTinyKeyChange = value => setStatusTinySituacao(value === 'outro' ? '' : value);

    // As automações do backend acham o status pela key; quem tem key é do sistema.
    const isSystemStatus = Boolean(statusKey);

    const navigate = useNavigate();

    useEffect(() => {
        getStatuses();
    }, [filters]);

    useEffect(() => {
        if(!showCreateStatus) clean();
    }, [showCreateStatus]);

    useEffect(() => {
        if(!showEditStatus) clean();
    }, [showEditStatus]);

    useEffect(() => {
        generateData();
    }, [statuses]);

    const generateData = () => {
        const header = ['Nome', 'Cor', 'Última atualização', 'Cadastrado em'];
        const rows = {};
        
        typeOptions.map(type => {
            rows[type.value] = [];
            sortStatuses(statuses).map(status => {
                if(status.type !== type.value) return;

                rows[type.value].push({
                    data: [
                        renderName(status),
                        <DotStatus color={status.color} size={15} />,
                        // status.default ? 'Sim' : 'Não',
                        moment(status.updated_at).format('DD/MM/YYYY HH:mm'),
                        moment(status.created_at).format('DD/MM/YYYY HH:mm')
                    ],
                    action: () => status.default ? openInfoStatus(status) : editStatus(status)
                })
            });
        })

        setData({header, rows});
    }

    const renderName = status => {
        const info = getStatusInfo(status);

        return (
            <span className="status-name">
                {status.name}
                {info &&
                    <span className={`status-tag ${info.badgeType}`}>
                        {info.badgeIcon && <Icon name={info.badgeIcon} size={12} strokeWidth={2.5} />}
                        {info.badge}
                    </span>
                }
            </span>
        );
    }

    const openInfoStatus = status => {
        setInfoStatus(status);
        setShowInfoStatus(true);
    }

    const editStatus = status => {
        setStatusId(status.id);
        setStatusName(status.name);
        setStatusType(status.type);
        setStatusDefault(status.default);
        setStatusColor(status.color);
        setStatusKey(status.key);
        setStatusTinySituacao(status.tiny_situacao ?? null);
        setShowEditStatus(true);
    }

    const getStatuses = () => {
        setStatuses([]);
        setLoading(true);

        api.get(`/status${filters}`).then(res => {
            setStatuses(res.data.resources);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    }

    const storeStatus = () => {
        setLoading(true);

        let data = {
            name: statusName,
            type: statusType,
            default: false,
            color: statusColor,
            tiny_situacao: statusType === 'order' ? statusTinySituacao : null
        };

        api.post(`/status`, data).then(res => {
            toast.success('Status cadastrado com sucesso');
            setShowCreateStatus(false);
            getStatuses();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const updateStatus = () => {
        setLoading(true);

        let data = {
            name: statusName,
            type: statusType,
            default: statusDefault,
            color: statusColor,
            tiny_situacao: statusType === 'order' ? statusTinySituacao : null
        };

        api.put(`/status/${statusId}`, data).then(res => {
            toast.success('Status atualizado com sucesso');
            setShowEditStatus(false);
            getStatuses();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    const openCreateStatus = () => {
        setStatusType(tabs[currentTab]);
        setShowCreateStatus(true);
    }

    const clean = () => {
        setStatusId('');
        setStatusName('');
        setStatusType('budget');
        setStatusDefault(false);
        setStatusColor('');
        setStatusKey(null);
        setStatusTinySituacao(null);
    }

    const deleteStatus = () => {
        setLoading(true);
    
        api.delete(`/status/${statusToDelete}`).then(res => {
            setStatusToDelete(null);
            setShowEditStatus(false);
            getStatuses();
        }).catch(error => {
            renderError(error);
            setLoading(false);
        });
    }

    return (
        <>
            
            <Modal
                title={`Cadastrar status`}
                show={showCreateStatus}
                close={() => setShowCreateStatus(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Cadastrar`}
                        svg={`save-white`}
                        action={storeStatus}
                        loading={loading} 
                    />
                }
            >
                <div className="section">
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome *`} value={statusName} change={setStatusName} />
                        </div>
                        {statusType === 'order' &&
                            <div className="col-6">
                                <InputSelect label={`Código no Tiny`} value={tinySelectValue} change={handleTinyKeyChange} options={tinyKeyOptions} />
                            </div>
                        }
                    </div>
                    {isCustomTinyKey &&
                        <div className="row">
                            <div className="col-6">
                                <Input type={`text`} label={`Código customizado *`} value={statusTinySituacao} change={setStatusTinySituacao} />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-6">
                            <InputSelect label={`Tipo *`} value={statusType} change={setStatusType} options={statusTypeOptions} />
                        </div>
                        <div className="col-6">
                            <InputColor label={`Cor *`} value={statusColor} change={setStatusColor} />
                        </div>
                        {/* <div className="col-6">
                            <InputSelect label={`Padrão *`} value={statusDefault} change={setStatusDefault} options={defaultOptions} />
                        </div> */}
                    </div>
                </div>
            </Modal>
            <Modal
                title={`Editar status`}
                show={showEditStatus}
                close={() => setShowEditStatus(false)}
                footer={
                    <>
                        {!isSystemStatus &&
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Excluir`}
                                svg={`delete-small`}
                                action={() => setStatusToDelete(statusId)}
                                loading={loading} 
                            />
                        }
                        <Button
                            type={`primary`}
                            size={`small`}
                            text={`Salvar alterações`}
                            svg={`save-white`}
                            action={updateStatus}
                            loading={loading}
                        />
                    </>
                }
            >
                <div className="section">
                    {editingSystemRule &&
                        <div className="status-automation-note">
                            <Icon name="zap" size={18} strokeWidth={2.25} />
                            <div>
                                <strong>Status com regra no pedido de compra</strong>
                                <p>{editingSystemRule} Nome e cor podem ser alterados, mas a regra continua vinculada a ele.</p>
                            </div>
                        </div>
                    }
                    {statusType === 'budget' && statusKey === 'pendente_aprovacao' &&
                        <div className="status-automation-note">
                            <Icon name="zap" size={18} strokeWidth={2.25} />
                            <div>
                                <strong>Status controlado por automação</strong>
                                <p>Este status é atribuído automaticamente pelo sistema: ao enviar uma proposta de um orçamento "Aguardando orçamento", ou quando um pedido é cancelado/excluído, o orçamento volta para cá. Nome e cor podem ser alterados, mas as automações continuam vinculadas a ele.</p>
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-6">
                            <Input type={`text`} label={`Nome *`} value={statusName} change={setStatusName} />
                        </div>
                        {statusType === 'order' &&
                            <div className="col-6">
                                <InputSelect label={`Código no Tiny`} value={tinySelectValue} change={handleTinyKeyChange} options={tinyKeyOptions} />
                            </div>
                        }
                    </div>
                    {isCustomTinyKey &&
                        <div className="row">
                            <div className="col-6">
                                <Input type={`text`} label={`Código customizado *`} value={statusTinySituacao} change={setStatusTinySituacao} />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="col-6">
                            <InputSelect label={`Tipo *`} value={statusType} change={setStatusType} options={typeOptions} isDisabled={isSystemStatus || isEditingPurchaseOrderStatus} />
                        </div>
                        <div className="col-6">
                            <InputColor label={`Cor *`} value={statusColor} change={setStatusColor} />
                        </div>
                        {/* <div className="col-6">
                            <InputSelect label={`Padrão *`} value={statusDefault} change={setStatusDefault} options={defaultOptions} />
                        </div> */}
                    </div>
                </div>
            </Modal>
            <ModalDelete
                show={statusToDelete !== null}
                close={() => setStatusToDelete(null)}
                action={deleteStatus}
                loading={loading}
            />
            <Modal
                className="modal-status-info"
                title={infoStatus ? infoStatus.name : 'Status padrão'}
                show={showInfoStatus}
                close={() => setShowInfoStatus(false)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Entendi`}
                        action={() => setShowInfoStatus(false)}
                    />
                }
            >
                <div className="section status-info">
                    {(() => {
                        const info = infoStatus ? STATUS_INFO[infoStatus.key] : null;
                        const paragraphs = info
                            ? info.description
                            : ['Este é um status padrão do sistema. Ele é necessário para o funcionamento da plataforma e, por isso, não pode ser editado nem excluído.'];

                        return paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>);
                    })()}
                </div>
            </Modal>
            <PanelTemplate id="status-list">
                <PageHeader
                    // left={
                    //     <FilterSimple 
                    //         params={[
                    //             {name: 'Nome', key: 'name', type: 'text' },
                    //         ]}
                    //         filter={setFilters}
                    //     />
                    // }

                    right={
                        <>
                            <Button type={`primary`} size={`small`} text={`Cadastrar status`} svg="plus-white" action={openCreateStatus} />
                        </>
                    }
                />
                <BoxTabs
                    tab={currentTab}
                    setTab={changeTab}
                    titles={[
                        'Orçamento',
                        'Pedido de venda',
                        ...(hasGestao ? ['Pedido de compra'] : [])
                    ]}
                    contents={[
                        <Table header={data.header} rows={data.rows.budget} loading={loading} />,
                        <Table header={data.header} rows={data.rows.order} loading={loading} />,
                        hasGestao && <>
                            <p className="status-tab-hint">
                                Os status aparecem no pedido de compra na ordem em que foram cadastrados.
                                Os marcados com <Icon name="zap" size={11} strokeWidth={2.5} /> ou <Icon name="lock" size={11} strokeWidth={2.5} /> têm regra no sistema: dá para mudar nome e cor, mas não excluir.
                            </p>
                            <Table header={data.header} rows={data.rows.purchase_order} loading={loading} />
                        </>
                    ]}
                />
            </PanelTemplate>
        </>
    );
}

export default StatusList;